import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import API from "@app/services/api";
import useUser from "@app/zustand/user";
import { readAuthPolicy } from "@app/utils/auth-policy";

type Step = "loading" | "off" | "on";

interface StatusResponse {
  set?: boolean;
  set_at?: string | null;
  min_length?: number;
}

interface MutationResponse {
  set?: boolean;
  detail?: string;
  error?: string;
}

interface PasswordSettingsProps {
  /** Tells the parent a password has just been set or removed. */
  onChanged?: () => void;
}

/**
 * The account's password.
 *
 * Self-contained: reads /user/password/status on mount, then drives setting,
 * changing and removing it. As soon as a password exists it replaces the emailed
 * code as the primary factor, and it is the prerequisite of application MFA.
 */
export default function PasswordSettings({ onChanged }: PasswordSettingsProps) {
  const { t } = useLingui();
  const [step, setStep] = useState<Step>("loading");
  const [minLength, setMinLength] = useState(12);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const user = useUser((state) => state.user);
  // The password is the required first factor: the server refuses to remove it on
  // any account the rule covers. Only an exempt one — the demo, or an account that
  // signs in with an e-CPS — keeps this button.
  const removable = Boolean(readAuthPolicy(user)?.exempt);

  useEffect(() => {
    API.get({ path: "/user/password/status" }).then((res: StatusResponse) => {
      setStep(res?.set ? "on" : "off");
      if (res?.min_length) setMinLength(res.min_length);
    });
  }, []);

  function resetFields() {
    setCurrent("");
    setNext("");
    setConfirm("");
    setRemoving(false);
  }

  function submit() {
    if (next !== confirm) {
      setError(t`Les deux mots de passe ne sont pas identiques.`);
      return;
    }
    setBusy(true);
    setError("");
    setSaved(false);
    API.post({
      path: "/user/password/set",
      body: {
        new_password: next,
        current_password: step === "on" ? current : undefined,
      },
    })
      .then((res: MutationResponse) => {
        if (res?.set) {
          setStep("on");
          resetFields();
          setSaved(true);
          onChanged?.();
        } else {
          setError(
            res?.detail ||
              res?.error ||
              t`Impossible d'enregistrer le mot de passe.`,
          );
        }
      })
      .finally(() => setBusy(false));
  }

  function remove() {
    setBusy(true);
    setError("");
    setSaved(false);
    API.post({
      path: "/user/password/remove",
      body: { current_password: current },
    })
      .then((res: MutationResponse) => {
        if (res?.set === false) {
          setStep("off");
          resetFields();
          onChanged?.();
        } else {
          setError(
            res?.detail ||
              res?.error ||
              t`Impossible de supprimer le mot de passe.`,
          );
        }
      })
      .finally(() => setBusy(false));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans>Mot de passe</Trans>
        </CardTitle>
        <CardDescription>
          <Trans>
            Le mot de passe est votre premier facteur de connexion : dès qu'il
            est défini, il remplace le code reçu par email. Il est également
            nécessaire pour activer la double authentification.
          </Trans>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === "loading" && (
          <p className="text-sm text-slate-500">
            <Trans>Chargement...</Trans>
          </p>
        )}

        {step !== "loading" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {step === "on" ? (
                <Trans>
                  ✓ Un mot de passe est défini. Vous vous connectez avec votre
                  email et votre mot de passe.
                </Trans>
              ) : !removable ? (
                <Trans>
                  Aucun mot de passe n'est défini. Il est obligatoire : le code
                  reçu par email n'est plus un moyen de connexion conforme.
                </Trans>
              ) : (
                <Trans>
                  Aucun mot de passe n'est défini. Vous recevez actuellement un
                  code par email à chaque connexion.
                </Trans>
              )}
            </p>

            {step === "on" && !removing && (
              <div className="space-y-2 max-w-sm">
                <Label htmlFor="password-current">
                  <Trans>Mot de passe actuel</Trans>
                </Label>
                <Input
                  id="password-current"
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </div>
            )}

            {!removing && (
              <>
                <div className="space-y-2 max-w-sm">
                  <Label htmlFor="password-new">
                    {step === "on" ? (
                      <Trans>Nouveau mot de passe</Trans>
                    ) : (
                      <Trans>Mot de passe</Trans>
                    )}
                  </Label>
                  <Input
                    id="password-new"
                    type="password"
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                  />
                  <p className="text-xs text-slate-500">
                    <Trans>
                      Au moins {minLength} caractères, combinant au moins trois
                      types parmi : minuscules, majuscules, chiffres, caractères
                      spéciaux.
                    </Trans>
                  </p>
                </div>
                <div className="space-y-2 max-w-sm">
                  <Label htmlFor="password-confirm">
                    <Trans>Confirmer le mot de passe</Trans>
                  </Label>
                  <Input
                    id="password-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>
              </>
            )}

            {removing && (
              <div className="space-y-2 max-w-sm">
                <Label htmlFor="password-remove-current">
                  <Trans>
                    Saisissez votre mot de passe actuel pour le supprimer
                  </Trans>
                </Label>
                <Input
                  id="password-remove-current"
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  <Trans>
                    Vous recevrez de nouveau un code par email à chaque
                    connexion.
                  </Trans>
                </p>
              </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
            {saved && (
              <p className="text-sm font-medium text-primary">
                <Trans>✓ Mot de passe enregistré.</Trans>
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              {!removing && (
                <Button
                  onClick={submit}
                  disabled={
                    busy ||
                    next.length < minLength ||
                    !confirm ||
                    (step === "on" && !current)
                  }
                  className="bg-primary text-white"
                >
                  {busy ? (
                    <Trans>Enregistrement...</Trans>
                  ) : step === "on" ? (
                    <Trans>Modifier le mot de passe</Trans>
                  ) : (
                    <Trans>Définir le mot de passe</Trans>
                  )}
                </Button>
              )}

              {step === "on" && !removing && removable && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setRemoving(true);
                    setError("");
                    setSaved(false);
                    setNext("");
                    setConfirm("");
                  }}
                  disabled={busy}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  <Trans>Supprimer le mot de passe</Trans>
                </Button>
              )}

              {removing && (
                <>
                  <Button
                    onClick={remove}
                    disabled={busy || !current}
                    className="border-red-300 bg-red-600 text-white hover:bg-red-700"
                  >
                    {busy ? (
                      <Trans>Suppression...</Trans>
                    ) : (
                      <Trans>Confirmer la suppression</Trans>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      resetFields();
                      setError("");
                    }}
                    disabled={busy}
                  >
                    <Trans>Annuler</Trans>
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
